import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import Raven from 'raven-js';
import { useEffect, useState } from 'react';
import { submitCTAClick } from 'self-service-api/api/submitCTAClick';
import { marketingPricing } from 'self-service-api/core/utilities/links';
import { UPGRADE_MODAL } from 'ui-addon-upgrades/_core/common/constants/PurchaseMotionLocations';
import { getIsFreePortalAndOwnedProducts } from 'ui-addon-upgrades/_core/common/eligibility/getIsFreePortal';
import { getPrioritizedUpgradeProductFromApiName } from 'ui-addon-upgrades/ums/getPrioritizedUpgradeProduct';
import { POINT_TYPES } from 'ui-addon-upgrades/ums/types';
import { validateModalUpgradeConfig } from 'ui-addon-upgrades/ums/utils';
import { getPurchaseMotionsByLocation } from 'pricing-pages-lib/api/PurchaseMotionsApi';
import { fetchUpgradeConfig } from 'pricing-pages-lib/api/fetchUpgradeConfig';
import { getQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { useHistory } from 'react-router-dom';
import { SALES_HUB_PROFESSIONAL } from 'self-service-api/constants/ApiNames';
import { UPGRADE_PAGE_APP } from '../constants/ErrorBoundaryNames';
import { useIsHC0124Variant } from '../hooks/hc0124Hooks';
import { useTrackRetail } from '../hooks/useTrackRetail';
import { useAppSelector } from '../reducers';
import { hasPricingAndPurchasingSucceededSelector } from '../selectors/LoadingStateSelectors';
import { Metrics } from '../utils/metrics';
import { getIsAcceptanceTest } from '../utils/testUtils';
import { withErrorBoundary } from './ErrorBoundary';
import AddonUpgradePage from './containers/AddonUpgradePage';
import HubProPlusUpgradePage from './containers/HubProPlusUpgradePage';
import ProPlusPageConditionalWrapper from './containers/HubProPlusUpgradePageConditionalWrapper';
import HubStarterUpgradePage from './containers/HubStarterUpgradePage';
import SuiteStarterUpgradePage from './containers/SuiteStarterUpgradePage';
import { SUB_PAGE_TYPE, _determineUpgradePageSubPage } from './utils/determineUpgradePageSubPage';
import { getPurchaseMotionEligibility } from './utils/getPurchaseMotionEligibility';
import { _getUMSUpgradeKey } from './utils/getUMSUpgradeKey';
import { saveLockedFeatureInteractionOnLocalStorage } from './utils/localStorageUtils';
import { jsx as _jsx } from "react/jsx-runtime";
export const NAV_UPGRADE_SOURCE = 'global-nav-bar';
const UpgradePageApp = () => {
  const isAcceptanceTest = getIsAcceptanceTest();
  useTrackRetail();
  const queryParams = getQueryParams();
  const upgradeSource = queryParams.upgradeSource;
  const umsUpgradeKey = _getUMSUpgradeKey(upgradeSource);
  const providedIndustry = queryParams.providedIndustry;
  const hasPricingAndPurchasingSucceeded = useAppSelector(hasPricingAndPurchasingSucceededSelector);
  const history = useHistory();
  const {
    isHC0124Variant,
    isHC0124Fetched
  } = useIsHC0124Variant();
  const [data, setData] = useState();
  useEffect(() => {
    Promise.all([fetchUpgradeConfig(umsUpgradeKey, providedIndustry), getIsFreePortalAndOwnedProducts(), getPurchaseMotionsByLocation([UPGRADE_MODAL])]).then(([upgradeConfigResponse, isFreePortalAndOwnedProducts, purchaseMotionsResponse]) => {
      if (upgradeConfigResponse.pointType === POINT_TYPES.MODAL) {
        validateModalUpgradeConfig(upgradeConfigResponse);
        saveLockedFeatureInteractionOnLocalStorage(umsUpgradeKey, upgradeConfigResponse.apiNames[0]);
      }
      const {
        recommendationResponse
      } = upgradeConfigResponse;
      const recommendedHub = recommendationResponse ? recommendationResponse.hubType : undefined;
      const selectedApiName = getPrioritizedUpgradeProductFromApiName(upgradeConfigResponse.apiNames, purchaseMotionsResponse.locations[UPGRADE_MODAL].purchaseMotions, isFreePortalAndOwnedProducts.ownedProducts, isFreePortalAndOwnedProducts.isFreePortal, recommendedHub);
      setData({
        upgradeConfig: upgradeConfigResponse,
        upgradePointApiName: selectedApiName,
        purchaseMotions: getPurchaseMotionEligibility(selectedApiName, purchaseMotionsResponse.locations[UPGRADE_MODAL].purchaseMotions),
        isFreePortal: isFreePortalAndOwnedProducts.isFreePortal
      });
    }).catch(error => {
      Raven.captureException(new Error('Unexpected error setting up the upgrade page'), {
        extra: {
          umsUpgradeKey,
          message: error.message
        }
      });
      Metrics.counter('upgrade-page-setup-error').increment();
      history.push(`/marketing?upgradeSource=${umsUpgradeKey}`);
    });
  }, [umsUpgradeKey, providedIndustry, history]);
  if (!data || !hasPricingAndPurchasingSucceeded || !isHC0124Fetched) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  const handlePurchaseMotionButtonClick = () => {
    const {
      recommendationResponse
    } = data.upgradeConfig;
    if (recommendationResponse && recommendationResponse.correlationId) {
      submitCTAClick(recommendationResponse.correlationId).catch(error => Raven.captureException(error, {
        extra: {
          message: 'Failed to submit CTA click of recommended hub'
        }
      }));
    }
  };
  switch (_determineUpgradePageSubPage(data.upgradePointApiName)) {
    case SUB_PAGE_TYPE.SUITE_STARTER_PPP:
      return /*#__PURE__*/_jsx(SuiteStarterUpgradePage, {
        upgradePointApiName: data.upgradePointApiName,
        upgradeConfig: data.upgradeConfig,
        purchaseMotions: data.purchaseMotions,
        handlePurchaseMotionButtonClick: handlePurchaseMotionButtonClick,
        isFreePortal: data.isFreePortal,
        upgradeSource: upgradeSource
      });
    case SUB_PAGE_TYPE.HUB_STARTER_PPP:
      return /*#__PURE__*/_jsx(HubStarterUpgradePage, {
        upgradePointApiName: data.upgradePointApiName,
        upgradeConfig: data.upgradeConfig,
        purchaseMotions: data.purchaseMotions,
        handlePurchaseMotionButtonClick: handlePurchaseMotionButtonClick,
        upgradeSource: upgradeSource
      });
    case SUB_PAGE_TYPE.HUB_PROFESSIONAL_PPP:
      if (data.upgradePointApiName === SALES_HUB_PROFESSIONAL && !isAcceptanceTest) {
        return /*#__PURE__*/_jsx(ProPlusPageConditionalWrapper, {
          handlePurchaseMotionButtonClick: handlePurchaseMotionButtonClick,
          hasPricingAndPurchasingSucceeded: hasPricingAndPurchasingSucceeded,
          purchaseMotions: data.purchaseMotions,
          upgradeConfig: data.upgradeConfig,
          upgradePointApiName: data.upgradePointApiName,
          upgradeSource: upgradeSource,
          isHC0124Variant: isHC0124Variant,
          isHC0124Fetched: isHC0124Fetched
        });
      }
      return /*#__PURE__*/_jsx(HubProPlusUpgradePage, {
        upgradePointApiName: data.upgradePointApiName,
        upgradeConfig: data.upgradeConfig,
        purchaseMotions: data.purchaseMotions,
        handlePurchaseMotionButtonClick: handlePurchaseMotionButtonClick,
        upgradeSource: upgradeSource
      });
    case SUB_PAGE_TYPE.HUB_ENTERPRISE_PPP:
      return /*#__PURE__*/_jsx(HubProPlusUpgradePage, {
        upgradePointApiName: data.upgradePointApiName,
        upgradeConfig: data.upgradeConfig,
        purchaseMotions: data.purchaseMotions,
        handlePurchaseMotionButtonClick: handlePurchaseMotionButtonClick,
        upgradeSource: upgradeSource
      });
    case SUB_PAGE_TYPE.ADDON_PPP:
      return /*#__PURE__*/_jsx(AddonUpgradePage, {
        upgradePointApiName: data.upgradePointApiName,
        upgradeConfig: data.upgradeConfig,
        purchaseMotions: data.purchaseMotions,
        handlePurchaseMotionButtonClick: handlePurchaseMotionButtonClick,
        upgradeSource: upgradeSource
      });
    default:
      history.push(`/marketing?upgradeSource=${umsUpgradeKey}`);
      return null;
  }
};
export default withErrorBoundary(UpgradePageApp, {
  boundaryName: UPGRADE_PAGE_APP,
  redirectCallback: marketingPricing
});